import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { LoginPage } from './components/LoginPage';
import { SessionProvider, useSession } from './context/SessionContext';
import { pathWithKeptQuery, resolveResumeSessionId } from './lib/sessionUrl';
import { CallPage } from './pages/CallPage';
import { CompletePage } from './pages/CompletePage';

function AuthLoading() {
  return (
    <div className="toast" role="status" style={{ display: 'block' }}>
      Loading…
    </div>
  );
}

/** Redirect that keeps ?agent= (and other kept keys) across path changes. */
function RedirectKeepAgent({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={pathWithKeptQuery(to, location.search)} replace />;
}

function RequireAuth() {
  const { authReady, profile } = useSession();
  const location = useLocation();
  if (!authReady) return <AuthLoading />;
  if (!profile?.isUser) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

function LoginRoute() {
  const { authReady, profile, refreshProfile, tryResume } = useSession();
  const location = useLocation();
  if (!authReady) return <AuthLoading />;
  if (profile?.isUser) {
    const sid = resolveResumeSessionId();
    if (sid) {
      tryResume();
      return <RedirectKeepAgent to="/conversation" />;
    }
    return <RedirectKeepAgent to="/haircheck-returning" />;
  }
  const from =
    (location.state as { from?: string } | null)?.from
    || pathWithKeptQuery('/haircheck-returning', location.search);
  return (
    <LoginPage
      onSuccess={() => {
        void refreshProfile().then(() => {
          const dest = from.startsWith('/')
            ? from
            : pathWithKeptQuery('/haircheck-returning', location.search);
          window.location.assign(dest);
        });
      }}
    />
  );
}

function AppRoutes() {
  // Stable element identity so haircheck ↔ conversation path changes never remount media.
  const callPage = <CallPage />;
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route element={<RequireAuth />}>
        <Route path="/haircheck-returning" element={callPage} />
        <Route path="/conversation" element={callPage} />
        <Route path="/complete" element={<CompletePage />} />
      </Route>
      <Route path="/" element={<RedirectKeepAgent to="/haircheck-returning" />} />
      <Route path="*" element={<RedirectKeepAgent to="/haircheck-returning" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <SessionProvider>
      <AppRoutes />
    </SessionProvider>
  );
}
